import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import {
  IErrorMessage,
  useErrorMessage
} from '@shared/hooks/use-error-message';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GenreAddDialog from '@modules/entertainment/components/genre-add-dialog/genre-add-dialog';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog';
import AppAutocompleteWithActions from '@shared/components/app-autocomplete-with-actions/app-autocomplete-with-actions';
import { GenresEnum } from '@modules/entertainment/models/entertainment.models';
import { LanguageEnum } from '@shared/models/language.model';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import useRadioStore from '../../store/radio.store';
import {
  RadioDialogFormProps,
  RadioGenreReqModel,
  RadioReqModel
} from '@modules/entertainment/models/radio.model';
import { CrudActionEnum } from '@models/app-autocomplete.model.ts';
import { validateNotOnlySpaces } from '@shared/utils/trim-form-values-utils';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import { FileTypeEnum } from '@models/media-library.model.ts';

const RadioDialogForm: React.FC<RadioDialogFormProps> = ({
  selectedItem,
  onSave,
  genreList
}) => {
  const {
    register,
    reset,
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      title: '',
      url: '',
      genres: []
    }
  });

  const getErrorMessage = useErrorMessage();

  const { t } = useTranslation();
  const { isLoading, createGenre, editGenre, deleteGenre, isGenreLoading } =
    useRadioStore();
  const { resetSelectedMedia } = useMediaLibraryStore();

  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);
  const [showAddNewGenreDialog, setShowAddNewGenreDialog] =
    React.useState(false);
  const [genresLists, setGenresLists] = React.useState<any[]>([]);
  const [selectedGenreValue, setSelectedGenreValue] = React.useState<{
    id?: string;
    name: string;
  }>({ id: '', name: '' });
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const errorMessages: IErrorMessage = {
    title: {
      required: 'validation_title_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    },
    url: {
      required: 'validation_author_name_required',
      pattern: 'validation_url_pattern',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    },
    genres: {
      required: 'validation_genres_required'
    }
  };

  const handleCloseGenreDialog = () => {
    resetSelectedMedia('genreImage');
    setShowAddNewGenreDialog(false);
    setShowDeleteDialog(false);
    setSelectedGenreValue({ id: '', name: '' });
  };

  const onSubmit = (data: RadioReqModel) => {
    const fullData: RadioReqModel = {
      title: data.title.trim(),
      url: data.url,
      image: selectedMedia.image?.path || null,
      genres: genresLists.map(({ id }) => id)
    };

    onSave(fullData);
  };

  const handleSaveGenre = async (value: RadioGenreReqModel) => {
    if (selectedGenreValue?.id) {
      editGenre({
        data: value,
        id: selectedGenreValue?.id,
        handleClose: handleCloseGenreDialog
      });
    } else {
      createGenre({
        data: value,
        handleClose: handleCloseGenreDialog
      });
    }
  };

  const handleDeleteConfirm = async () => {
    deleteGenre({
      id: selectedGenreValue?.id,
      handleClose: () => setShowDeleteDialog(false)
    });
  };

  const itemActionHandler = (value: any, type: CrudActionEnum) => {
    if (type === CrudActionEnum.Add) {
      setSelectedGenreValue({ name: value });
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Edit) {
      setSelectedGenreValue(value);
      setShowAddNewGenreDialog(true);
    } else if (type === CrudActionEnum.Delete) {
      setSelectedGenreValue(value);
      setShowDeleteDialog(true);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const { title, url } = selectedItem;

      const genreObjects =
        selectedItem.genres?.map((genre: any) => ({
          ...genre,
          id: genre._id,
          name: genre.name[LanguageEnum.English] || Object.values(genre.name)[0]
        })) || [];

      reset({
        title: title || '',
        url: url || '',
        genres: genreObjects
      });

      setGenresLists(genreObjects);
    }
  }, [selectedItem, reset]);

  return (
    <div>
      <form className="flex flex-col gap-1">
        <div className="media-wrapper mb-3">
          <div className="flex mb-1.5">
            <span className="reuqired-symbole">*</span>
            {t('label_radio_image')}
            <InfoTooltip text="tooltip_info_radio_image" />
          </div>

          <MediaLibraryComponent
            mediaKey={'image'}
            width={240}
            height={120}
            allowedTypes={[FileTypeEnum.Image]}
            path={selectedItem?.image}
            onSelect={(media) =>
              setSelectedMedia((prev: any) => ({
                ...prev,
                image: media
              }))
            }
          />
        </div>

        <FormControl error={!!errors.title}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_radio_title')}
            <InfoTooltip text="tooltip_info_radio_title" />
          </FormLabel>
          <OutlinedInput
            {...register('title', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.title}
            placeholder={t('label_radio_title_placeholder')}
          />
          <FormHelperText>
            {errors?.title &&
              getErrorMessage({
                errors,
                fieldName: 'title',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <FormControl error={!!errors.url}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_radio_url')}
            <InfoTooltip text="tooltip_info_radio_stream_url" />
          </FormLabel>
          <OutlinedInput
            {...register('url', {
              required: true,
              pattern:
                /^(https?:\/\/)([a-z\d.-]+)(\.[a-z\d.-]+)+(:\d+)?(\/[^\s]*)?$/i,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.url}
            placeholder={t('label_radio_url_placeholder')}
          />
          <FormHelperText>
            {errors?.url &&
              getErrorMessage({
                errors,
                fieldName: 'url',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <AppAutocompleteWithActions
          name="genres"
          control={control}
          label={
            <span className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_category')}
              <InfoTooltip text="tooltip_info_radio_genres" />
            </span>
          }
          options={genreList || []}
          selectedList={genresLists || []}
          onChange={setGenresLists}
          itemActionHandler={itemActionHandler}
          placeholder={t('label_radio_genres_placeholder')}
          validationProps={{
            errors,
            errorMessages,
            getErrorMessage,
            validationRules: { required: true }
          }}
        />

        <div className="flex justify-end items-center gap-6 pt-4 pb-1 sticky bottom-0 bg-white">
          <Button
            disabled={!isValid || !selectedMedia?.image}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            loading={isLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>

      <AppDialog
        open={showAddNewGenreDialog}
        title={t('entertainment_add_category_title')}
        content={
          <GenreAddDialog
            loading={isGenreLoading()}
            selectedValue={selectedGenreValue}
            type={GenresEnum.Radio}
            handleSave={handleSaveGenre}
            showLanguageTabs={false}
          />
        }
        onClose={handleCloseGenreDialog}
      />

      <div className="w-full h-full">
        <DeleteDialog
          onCancel={handleCloseGenreDialog}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          title={t('delete_popup_title')}
          isLoading={isGenreLoading()}
        />
      </div>
    </div>
  );
};

export default RadioDialogForm;
